/**
 * Data-driven NPC schedule runner (owner: env-builder). Ticked on the fixed step by WorldScene;
 * pure enough to unit-test with a fake actor. Steps come from `MapNpcPlacement.schedule` and the
 * optional `weatherBehaviour[state]` override that replaces the schedule from the moment a weather
 * transition *starts* towards that state (WorldScene calls `setWeather` on the `t = 0` emission —
 * see `beginWeather` in sceneHelpers.ts; the characters runner reaches the same state from the bus).
 */
import type { NpcScheduleStep } from '@/core/types/contracts';
import type { Facing, WeatherState } from '@/core/types';

export interface ScheduledActor {
  walkTo(x: number, y: number, speedPxPerSec?: number): Promise<void>;
  stop(): void;
  setFacing(f: Facing): void;
  showEmote(emote: string, ms?: number): Promise<void>;
}

const MAX_INSTANT_STEPS_PER_TICK = 8;

export class NpcScheduleRunner {
  private steps: NpcScheduleStep[];
  private index = 0;
  private waitLeft = 0;
  private busy = false;
  private paused = false;
  private token = 0;
  private destroyed = false;
  private currentWeather: WeatherState | null = null;

  constructor(
    private readonly actor: ScheduledActor,
    private readonly base: NpcScheduleStep[],
    private readonly weatherBehaviour: Partial<Record<WeatherState, NpcScheduleStep[]>> = {},
  ) {
    this.steps = base;
  }

  get stepIndex(): number { return this.index; }
  get isBusy(): boolean { return this.busy; }
  get isPaused(): boolean { return this.paused; }
  get activeSteps(): readonly NpcScheduleStep[] { return this.steps; }

  /** Called by WorldScene when a transition towards `state` starts (t = 0; instantly for t = 1). Switches to the override list when present. */
  setWeather(state: WeatherState): void {
    if (this.currentWeather === state) return;
    this.currentWeather = state;
    const next = this.weatherBehaviour[state] ?? this.base;
    if (next === this.steps) return;
    this.cancelCurrent();
    this.steps = next;
    this.index = 0;
    this.waitLeft = 0;
  }

  setPaused(on: boolean): void {
    if (this.paused === on) return;
    this.paused = on;
    if (on) this.cancelCurrent(); // a walk in progress is re-issued on resume
  }

  tick(dtMs: number): void {
    if (this.destroyed || this.paused || this.busy || this.steps.length === 0) return;
    if (this.waitLeft > 0) {
      this.waitLeft -= dtMs;
      if (this.waitLeft > 0) return;
      this.waitLeft = 0;
    }
    for (let guard = 0; guard < MAX_INSTANT_STEPS_PER_TICK; guard++) {
      const step = this.steps[this.index];
      if (!step) { this.index = 0; return; }
      switch (step.kind) {
        case 'wait':
          this.waitLeft = Math.max(0, step.ms ?? 0);
          this.advance();
          return;
        case 'face':
          if (step.facing) this.actor.setFacing(step.facing);
          this.advance();
          continue;
        case 'loop':
          // A loop is a yield point: the schedule restarts on the next tick (never spins within one).
          this.index = 0;
          return;
        case 'walk': {
          if (step.x === undefined || step.y === undefined) { this.advance(); continue; }
          this.busy = true;
          const token = ++this.token;
          this.safe(this.actor.walkTo(step.x, step.y)).then(() => this.finish(token));
          return;
        }
        case 'emote': {
          this.busy = true;
          const token = ++this.token;
          this.safe(this.actor.showEmote(step.emote ?? '?', step.ms)).then(() => this.finish(token));
          return;
        }
        default:
          this.advance();
          continue;
      }
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.cancelCurrent();
  }

  private finish(token: number): void {
    if (token !== this.token || this.destroyed) return;
    this.busy = false;
    this.advance();
  }

  private advance(): void {
    this.index = (this.index + 1) % Math.max(1, this.steps.length);
  }

  private cancelCurrent(): void {
    if (this.busy) {
      this.token++;
      this.busy = false;
      try { this.actor.stop(); } catch { /* actor may be gone */ }
    }
  }

  private safe(p: Promise<void>): Promise<void> {
    return Promise.resolve(p).catch((e: unknown) => { console.warn('[world] npc schedule step failed', e); });
  }
}
